import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import locale from "./modules/locale";

Vue.use(Vuex);

const debug = process.env.NODE_ENV !== 'production'

export default new Vuex.Store({
    modules: {
        locale
    },
    strict: debug,
    state : {
        app : {
            store: window.tebex.store,
            year: new Date().getFullYear(),
            step: 1
        },
        basket : {
            coupons: [],
            items: null,
            giftcards: [],
            price: 0,
            tax: 0,
            recurring: false,
            recurringPeriod: '',
            recurringNextPaymentDate: null,
            isPaymentMethodUpdate: false,
            returnUrl: '',
            creatorCode: '',
            recurringItems: null,
        },
        roundUp : {},
        customer : {
            first_name: "",
            last_name: "",
            email: "",
            create_account: false,
            country: 'XX',
            postal_code: false
        },
        payment : {
            selectedMethod: null,
            paymentMethodData: null,
            paymentMethodLoading: false,
            setupResponse: null,
            gdprConsent: false,
            doRoundUp: false
        }
    },
    mutations: {
        nextStep: state => {
            state.app.step++;
        },
        previousStep: state => {
            state.app.step--;
        },
        setStep: (state, step) => {
            state.app.step = step;
        },
        setBasket: (state, basket) => {
            state.customer.country = basket.country;
            state.basket = basket;
        },
        setRoundUp: (state, roundUp) => {
            state.roundUp = roundUp;
        },
        setCustomer: (state, customer) => {
            state.customer = customer;
        },
        setCountry: (state, countryCode) => {
            state.customer.country = countryCode;
        },
        setPaymentMethod: (state, paymentMethod) => {
            state.payment.selectedMethod = paymentMethod;
        },
        setPaymentMethodLoading: (state, loading) => {
            state.payment.paymentMethodLoading = loading;
        },
        setPaymentMethodData: (state, paymentMethodData) => {
            state.payment.paymentMethodData = paymentMethodData;
        },
        setSetupResponse: (state, setupResponse) => {
            state.payment.setupResponse = setupResponse;
        },
        setGdprConsent: (state, hasGdprConsent) => {
            state.payment.gdprConsent = hasGdprConsent;
        },
        setDoRoundUp: (state, doRoundUp) => {
            state.payment.doRoundUp = doRoundUp;
        }
    },
    actions : {
        fetchBasket ({ commit }) {
            return axios.get(tebex.application.basketApi)
                .then(response => {
                    let basket = {
                        price: response.data.priceDetails,
                        tax: response.data.tax,
                        recurring: response.data.recurring,
                        recurringPeriod: response.data.recurringPeriod,
                        isPaymentMethodUpdate: response.data.isPaymentMethodUpdate,
                        recurringNextPaymentDate: response.data.recurringNextPaymentDate,
                        returnUrl: response.data.returnUrl,
                        recurringItems: response.data.recurring_items,
                    };
                    let items = [];
                    let coupons = [];
                    for (let i=0;i<response.data.rows.length;i++) {
                        let item = response.data.rows[i];
                        items[i] = {
                            id : item.id,
                            name : item.meta.name,
                            qty : item.quantity,
                            price : item.price,
                            rowprice : item.meta.rowprice,
                            initialprice : item.meta.initialprice,
                            package: item.package
                        }
                    }

                    for (let i=0;i<response.data.priceDetails.discounts.length;i++) {
                        let coupon = response.data.priceDetails.discounts[i];
                        coupons[i] = {
                            id : coupon.id,
                            code : coupon.code,
                            saving : coupon.discountAmount,
                            type: coupon.type
                        }
                    }

                    basket.items = items;
                    basket.coupons = coupons;
                    basket.giftcards = response.data.giftcards;
                    basket.creatorCode = response.data.creator_code;

                    commit('setBasket', basket);

                    commit('setRoundUp', response.data.roundup);

                    let address = response.data.address;

                    commit('setCustomer', {
                        first_name: address.first_name,
                        last_name: address.last_name,
                        email: address.email,
                        country: address.country,
                        postal_code: address.postal_code
                    });
                })
        },
        addGiftCard ({ commit, dispatch }, cardNumber) {
            return axios.post(tebex.application.basketApi + "/giftcards/", { cardNumber }).then(() => {
                return dispatch('fetchBasket');
            });
        },
        addCoupon ({ commit, dispatch }, couponCode) {
            return axios.post(tebex.application.basketApi + "/coupons/", { couponCode }).then(() => {
                return dispatch('fetchBasket');
            });
        },
        addUpsellPackage({ commit, dispatch }, args) {
            return axios.post(tebex.application.basketApi + "/upsells/", {
                uuid: args['uuid'],
                options: args['options']
            }).then(() => {
                return dispatch('fetchBasket');
            });
        },
        removeCoupon ({ commit, dispatch }, couponId) {
            return axios.delete(tebex.application.basketApi + "/coupons/" + couponId).then(() => {
                return dispatch('fetchBasket');
            });
        },
        removeGiftCard ({ commit, dispatch }, giftCardId) {
            return axios.delete(tebex.application.basketApi + "/giftcards/" + giftCardId).then(() => {
                return dispatch('fetchBasket');
            });
        },
        addCreatorCode ({ commit, dispatch }, creatorCode) {
            return axios.post(tebex.application.basketApi + "/creator-code/", { creatorCode }).then((response) => {
                dispatch('fetchBasket');
                return response;
            });
        },
        updateCustomer({ commit, state, dispatch }, customer) {
            return axios.put(tebex.application.basketApi, {
                name : customer.first_name + " " + customer.last_name,
                first_name : customer.first_name,
                last_name : customer.last_name,
                email : customer.email,
                postal_code: customer.postal_code
            }).then(() => {
                commit('setCustomer', { ...state.customer,
                    email: customer.email,
                    first_name: customer.first_name,
                    last_name: customer.last_name,
                    postal_code: customer.postal_code
                });
                dispatch('fetchBasket');
            });
        },
        updateCountry({ commit, dispatch }, country) {
            return axios.put(tebex.application.basketApi, { country }).then(() => {
                commit('setCountry', country);
                dispatch('fetchBasket');
            });
        },
        selectPaymentMethod({ commit, state }, ident) {
            if (state.payment.paymentMethodLoading) {
                return;
            }

            if (state.payment.selectedMethod && state.payment.selectedMethod.ident === ident) {
                return;
            }

            commit('setPaymentMethodLoading', true);
            commit('setPaymentMethod', null);

            return axios.get(tebex.application.basketApi + "/payment/method/" + ident).then(r =>
                commit('setPaymentMethod', r.data)
            ).finally(() =>
                commit('setPaymentMethodLoading', false)
            );
        },
        checkout({ commit, state }) {
            let args = state.payment.paymentMethodData.data;
            args.gdprConsent = state.payment.gdprConsent;
            args.doRoundUp = state.payment.doRoundUp;
            return axios.post(tebex.application.basketApi + "/checkout", args).then(r => {
                commit('setSetupResponse', r.data);
            });
        },
        updatePaymentMethod({ commit, state }) {
            let args = state.payment.paymentMethodData.data;
            return axios.post(tebex.application.basketApi + "/payment-method", args).then(r => {
                commit('setSetupResponse', r.data);
            });
        },
        removeBasketItem ({ commit, dispatch }, basketItemId) {
            return axios.delete(tebex.application.basketApi + "/packages/" + basketItemId).then(() => {
                return dispatch('fetchBasket');
            });
        },
        validatePackageOptions ({ commit, dispatch }, args) {
            return axios.post(tebex.application.basketApi + "/packages/" + args['package'] + "/options/validate", {
                options: args['options']
            });
        },
        changeStep({ commit }, step) {
            commit('setStep', step);
        }
    }
});
